export const privacyPolicyContent = {
  'en-US': {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: May 21, 2024',
    description:
      'This privacy policy explains how the eyuanquant website ("we", "us", or "the Platform") collects, uses, shares, and protects your personal information. Please read it carefully before using our services.',
    sections: [
      {
        id: 'introduction',
        title: '1. Introduction',
        paragraphs: [
          'Welcome to the eyuanquant website. We respect your privacy and are committed to protecting your personal information. This policy clarifies what data we collect when you use our products and services, how we use, store, share, and transfer that information, and how you can access, update, delete, and safeguard your data.'
        ]
      },
      {
        id: 'data-collection',
        title: '2. Information We Collect',
        paragraphs: [
          'To provide our services, we may collect the following types of information:'
        ],
        list: [
          {
            title: 'Account information:',
            body: 'When you register through Casdoor or other methods, you need to provide an email address as your login name and set a password (managed by Casdoor). You may optionally provide a nickname or other profile details. We obtain your user identifier, email address, and the nickname/username you authorize from Casdoor.'
          },
          {
            title: 'Configuration data:',
            body: 'When you use the platform, we record configuration details such as selected symbols, time ranges, trading strategies, parameter settings, and any resulting outputs generated during your sessions.'
          },
          {
            title: 'User feedback:',
            body: 'When you submit feedback, we collect the feedback type you select, the description you provide, and any optional attachment file names (we do not currently store the attachment content itself).'
          },
          {
            title: 'Device and log information:',
            body: 'When you use the Platform we may automatically collect technical information such as your IP address, browser type and version, operating system, access time, and in-product activity logs (for example page views or feature usage). This information helps us analyse usage patterns and keep the service secure.'
          }
        ],
        closing: [
          'We will not collect your actual financial account credentials, bank card details, detailed transaction records, or other highly sensitive personal information unless we have your explicit consent or are required to do so by applicable laws and regulations.'
        ]
      },
      {
        id: 'data-usage',
        title: '3. How We Use Information',
        paragraphs: [
          'We primarily use your information for the following purposes:'
        ],
        list: [
          'Providing, maintaining, and improving core platform features and services, such as running your trading simulations and saving configurations and results.',
          'Performing identity verification (via Casdoor), customer support, security prevention, archiving, and backups to ensure the safety of the products and services we provide to you.',
          'Communicating with you, including sending important service notifications (for example simulation completion, system updates, or maintenance notices), technical updates, or responses to your inquiries and feedback.',
          'Conducting internal data analysis and research (for example aggregated analysis of popular strategies or parameter distributions, without targeting individual users) to improve product design, enhance user experience, and develop new features.',
          'With your explicit consent, sending you information about products or services you may be interested in.'
        ]
      },
      {
        id: 'data-sharing-transfer-disclosure',
        title: '4. Sharing, Transfer, and Disclosure',
        paragraphs: [
          'We do not share your personal information with companies, organisations, or individuals outside the Platform’s service providers except in the following situations:'
        ],
        list: [
          {
            title: 'With your explicit consent:',
            body: 'We will share your personal information with third parties only after obtaining your explicit approval.'
          },
          {
            title: 'Legal obligations:',
            body: 'We may share your personal information when required by laws, regulations, legal proceedings, or competent authorities for dispute resolution.'
          },
          {
            title: 'Affiliated companies:',
            body: 'We may share necessary personal information within our affiliated companies. The shared information remains bound by this policy. If an affiliate needs to change the processing purpose, it will ask for your authorisation again.'
          },
          {
            title: 'Authorised partners:',
            body: 'Some services may be provided jointly with authorised partners. We may share the minimum necessary personal information with those partners to deliver better customer service and user experience. They are not allowed to use the information for any purposes unrelated to the service.'
          }
        ],
        extraLists: [
          {
            intro:
              'We do not transfer your personal information to other companies, organisations, or individuals except in the following situations:',
            items: [
              'You give explicit consent for the transfer.',
              'If a merger, acquisition, or bankruptcy occurs that involves personal information transfer, we will require the new entity to continue to comply with this policy; otherwise, we will ask the new entity to obtain your authorisation again.'
            ]
          },
          {
            intro: 'We will only disclose your personal information publicly in the following circumstances:',
            items: [
              'With your explicit consent or at your request.',
              'When required by law, regulation, lawful procedures, or government authorities within the permitted scope and upon receipt of valid legal documentation.'
            ]
          }
        ]
      },
      {
        id: 'data-protection',
        title: '5. How We Protect Information',
        paragraphs: [
          'We employ industry-standard technical and organisational measures to protect your personal information from unauthorised access, disclosure, use, modification, damage, or loss. Examples include using encryption technologies such as SSL/TLS during transmission, enforcing strict access controls so only authorised personnel can view personal data, and providing regular security and privacy training to our staff.',
          'Despite our efforts and compliance with legal requirements, please understand that no security measures can guarantee absolute protection in the internet environment. The systems and communication networks you use to access our services may encounter issues outside our control.'
        ]
      },
      {
        id: 'user-rights',
        title: '6. Your Rights',
        paragraphs: [
          'Subject to applicable laws and regulations, you have the following rights regarding your personal information:'
        ],
        list: [
          {
            title: 'Right of access:',
            body: 'You can access your account details (managed through Casdoor), simulation history, collections, and other records.'
          },
          {
            title: 'Right of rectification:',
            body: 'If you identify inaccuracies in the personal information we process about you, you may request corrections or supplements (most changes can be made directly via Casdoor or in-product settings).'
          },
          {
            title: 'Right of erasure:',
            body: 'You may ask us to delete your personal information under certain circumstances, for example if you no longer use our services or if our processing violates laws or regulations.'
          },
          {
            title: 'Right to withdraw consent:',
            body: 'Where processing relies on your consent, you may withdraw it at any time.'
          },
          {
            title: 'Right to account closure:',
            body: 'You may request to close your account. After the account is closed, we will stop providing products or services to you and delete your personal information upon request, unless otherwise required by law.'
          }
        ],
        closing: [
          'You can exercise these rights through the profile settings within the Platform (for certain data), the Casdoor account management page, or by contacting us using the details provided at the end of this policy. To protect your security, we may ask you to verify your identity before handling the request.'
        ]
      },
      {
        id: 'cookies-tech',
        title: '7. Cookies and Similar Technologies',
        paragraphs: [
          'To ensure the site operates properly, provide a smoother experience, and recommend content you may find interesting, we store small data files called cookies on your computer or mobile device. Cookies typically contain identifiers, site names, and certain numbers and characters. They allow the site to remember your preferences (such as login status or interface language).',
          'You can manage or delete cookies based on your preferences. Most browsers let you clear cached data. Disabling cookies may affect your experience or cause certain services to stop working.'
        ]
      },
      {
        id: 'policy-updates',
        title: '8. Policy Updates',
        paragraphs: [
          'We may update this privacy policy from time to time. When changes occur, we will display the updated policy through page prompts, announcements, or pop-up notices. For material changes, we will provide a more prominent notice.',
          'We will collect, use, process, and store your personal information in accordance with the updated policy only after you confirm your acceptance. You may refuse the changes, but please note that doing so may prevent you from using certain features of the Platform.'
        ]
      },
      {
        id: 'contact-us',
        title: '9. Contact Us',
        paragraphs: [
          {
            type: 'contact',
            intro:
              'If you have any questions, comments, or suggestions about this privacy policy, or if you would like to exercise your rights, please contact us at ',
            email: 'support@eyuanquant.com',
            connector: ' or submit a request through our ',
            linkLabel: 'feedback portal',
            linkTo: '/feedback',
            ending: '.'
          },
          'We will review your request as soon as possible and respond within fifteen business days after verifying your identity.'
        ]
      }
    ]
  },
  'zh-CN': {
    title: '隐私政策',
    lastUpdated: '最后更新日期：2024年5月21日',
    description:
      '本隐私政策描述了 eyuanquant 网站（以下简称“我们”或“本平台”）如何收集、使用、共享和保护您的个人信息。请在使用我们的服务前仔细阅读。',
    sections: [
      {
        id: 'introduction',
        title: '1. 引言',
        paragraphs: [
          '欢迎访问 eyuanquant 网站。我们非常重视您的隐私权，并致力于保护您的个人信息安全。本隐私政策旨在向您清晰地说明，在您使用我们的产品/服务时，我们如何收集、使用、存储、共享和转让这些信息，以及我们为您提供的访问、更新、删除和保护这些信息的方式。'
        ]
      },
      {
        id: 'data-collection',
        title: '2. 我们收集的信息',
        paragraphs: ['为了向您提供服务，我们可能会收集以下类型的信息：'],
        list: [
          {
            title: '账户信息：',
            body: '当您通过 Casdoor 或其他方式注册账户时，您需要提供电子邮箱地址作为登录名，并设置密码（由 Casdoor 管理）。您也可以选择提供昵称等信息。我们从 Casdoor 获取您的用户标识符、邮箱和您授权的昵称/用户名。'
          },
          {
            title: '参数与数据：',
            body: '您在使用平台时创建的配置，包括但不限于选择的标的代码、时间周期、交易策略、参数设置等。平台会记录这些配置及产生的结果。'
          },
          {
            title: '用户反馈信息：',
            body: '当您通过平台的反馈渠道提交问题或建议时，我们会收集您的反馈内容，包括您选择的反馈类型、描述以及可选的附件文件名（附件本身目前不直接存储）。'
          },
          {
            title: '设备与日志信息：',
            body: '当您使用本平台时，我们可能会自动收集某些技术信息，例如您的 IP 地址、浏览器类型与版本、操作系统、访问日期和时间、以及您在平台内的操作记录（如页面浏览、功能点击等），这些信息用于分析平台使用情况和保障服务安全。'
          }
        ],
        closing: [
          '我们承诺，除非获取您的明确同意或法律法规另有规定，我们不会收集您的真实金融账户信息、银行卡信息、详细交易记录等高度敏感个人信息。'
        ]
      },
      {
        id: 'data-usage',
        title: '3. 信息的使用',
        paragraphs: ['我们收集您的信息主要用于以下目的：'],
        list: [
          '向您提供、维护和改进平台的核心功能和服务，例如创建和运行您的交易模拟，保存您的配置和结果。',
          '进行身份验证（通过 Casdoor）、客户服务、安全防范、存档和备份，确保我们向您提供的产品和服务的安全性。',
          '与您沟通，例如发送重要的服务通知（如模拟完成、系统更新、维护公告）、技术更新或回复您的咨询与反馈。',
          '进行内部数据分析和研究（例如，分析常用策略、模拟参数分布等，但不会针对个人），以改进我们的产品设计、优化用户体验、开发新功能。',
          '在获得您明确同意的情况下，向您发送您可能感兴趣的产品或服务信息。'
        ]
      },
      {
        id: 'data-sharing-transfer-disclosure',
        title: '4. 信息的共享、转让和公开披露',
        paragraphs: ['我们不会与本平台服务提供者以外的公司、组织和个人共享您的个人信息，但以下情况除外：'],
        list: [
          {
            title: '获得您的明确同意：',
            body: '在获得您的明确同意后，我们会与其他方共享您的个人信息。'
          },
          {
            title: '法定情形下的共享：',
            body: '我们可能会根据法律法规规定、诉讼争议解决需要，或按行政、司法机关依法提出的要求，对外共享您的个人信息。'
          },
          {
            title: '与关联公司共享：',
            body: '您的信息可能会在我们的关联公司内部共享。我们只会共享必要的个人信息，且受本隐私政策中所声明目的的约束。关联公司如要改变个人信息的处理目的，将再次征求您的授权同意。'
          },
          {
            title: '与授权合作伙伴共享：',
            body: '仅为实现本政策中声明的目的，我们的某些服务将由我们和授权合作伙伴共同提供。我们可能会与合作伙伴共享您的某些个人信息，以提供更好的客户服务和用户体验。我们仅会出于合法、正当、必要、特定、明确的目的共享您的个人信息，并且只会共享提供服务所必要的个人信息。我们的合作伙伴无权将共享的个人信息用于与产品或服务无关的其他用途。'
          }
        ],
        extraLists: [
          {
            intro: '我们不会将您的个人信息转让给任何公司、组织和个人，但以下情况除外：',
            items: [
              '在获取您的明确同意的情况下转让。',
              '在涉及合并、收购或破产清算等情形时，如涉及到个人信息转让，我们会要求新的持有您个人信息的公司、组织继续受本政策的约束，否则我们将要求该公司、组织重新向您征求授权同意。'
            ]
          },
          {
            intro: '我们仅会在以下情况下公开披露您的个人信息：',
            items: [
              '获得您明确同意或基于您的主动选择。',
              '如果执法部门、监管机构、政府部门或法院要求我们披露您的个人信息，我们仅会在法律允许的范围内，并在收到具备法律效力的正式文件后才会披露。'
            ]
          }
        ]
      },
      {
        id: 'data-protection',
        title: '5. 信息的保护',
        paragraphs: [
          '我们采用符合行业标准的安全技术和管理措施来保护您的个人信息，以防止数据遭到未经授权的访问、公开披露、使用、修改、损坏或丢失。例如，我们会使用加密技术（如 SSL/TLS）确保数据在传输过程中的安全性；我们会部署严格的访问控制机制，确保只有授权人员才可访问个人信息；我们会定期举办安全和隐私保护培训课程，加强员工对于保护个人信息重要性的认识。',
          '尽管我们已采取上述合理有效措施，并已遵守相关法律规定要求的标准，但请理解，由于技术的限制以及可能存在的各种恶意手段，在互联网行业，即便竭尽所能加强安全措施，也不可能始终保证信息百分之百的安全。您需要了解，您接入我们的服务所用的系统和通讯网络，有可能因我们可控范围外的因素而出现问题。'
        ]
      },
      {
        id: 'user-rights',
        title: '6. 您的权利',
        paragraphs: ['根据相关法律法规，我们保障您对自己的个人信息行使以下权利：'],
        list: [
          {
            title: '访问权：',
            body: '您有权访问您的账户信息（通过 Casdoor 管理）、模拟历史、收藏集等。'
          },
          {
            title: '更正权：',
            body: '当您发现我们处理的关于您的个人信息有错误时，您有权要求我们做出更正或补充（大部分可通过 Casdoor 或平台内功能自行操作）。'
          },
          {
            title: '删除权：',
            body: '在某些情况下，例如您不再使用我们的服务或我们处理您信息的行为违反了法律法规，您可以请求我们删除您的个人信息。'
          },
          {
            title: '撤回同意权：',
            body: '对于需要您同意才能处理的个人信息，您可以随时撤回您的同意。'
          },
          {
            title: '账户注销权：',
            body: '您有权注销您的平台账户。账户注销后，我们将停止为您提供产品或服务，并依据您的要求，删除您的个人信息，法律法规另有规定的除外。'
          }
        ],
        closing: [
          '您可以通过平台的个人资料设置页面（部分信息）、Casdoor 账户管理页面或通过本政策末尾提供的联系方式与我们联系，以行使您的上述权利。为保障安全，我们可能会先要求您验证自己的身份。'
        ]
      },
      {
        id: 'cookies-tech',
        title: '7. Cookie 与类似技术',
        paragraphs: [
          '为确保网站正常运转、为您获得更轻松的访问体验、向您推荐您可能感兴趣的内容，我们会在您的计算机或移动设备上存储名为 Cookie 的小数据文件。Cookie 通常包含标识符、站点名称以及一些号码和字符。借助于 Cookie，网站能够存储您的偏好（如登录状态、界面语言）等数据。',
          '您可根据自己的偏好管理或删除 Cookie。多数浏览器均为用户提供了清除浏览器缓存数据的功能，您可以在浏览器设置功能中进行相应的数据清除操作。但请注意，如果停用 Cookie，您可能无法享受最佳的服务体验，某些服务也可能无法正常使用。'
        ]
      },
      {
        id: 'policy-updates',
        title: '8. 政策更新',
        paragraphs: [
          '我们可能会适时对本隐私政策进行修订。当隐私政策的条款发生变更时，我们会在版本更新时以页面提示、公告、弹窗等形式向您展示变更后的隐私政策。对于重大变更，我们还会提供更为显著的通知。',
          '请您注意，只有在您确认同意变更后的隐私政策的全部内容后，我们才会按照更新后的隐私政策收集、使用、处理和存储您的个人信息；您有权拒绝同意变更后的隐私政策，但请知悉，一旦您拒绝同意变更，可能导致您无法使用本平台的某些功能。'
        ]
      },
      {
        id: 'contact-us',
        title: '9. 联系我们',
        paragraphs: [
          {
            type: 'contact',
            intro:
              '如果您对本隐私政策有任何疑问、意见或建议，或希望行使您的用户权利，请通过以下方式与我们联系：',
            email: 'support@eyuanquant.com',
            connector: ' 或 ',
            linkLabel: '用户反馈渠道',
            linkTo: '/feedback',
            ending: '。'
          },
          '我们将尽快审核所涉问题，并在验证您的用户身份后的十五个工作日内予以回复。'
        ]
      }
    ]
  }
};
